import type { ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Button, Modal } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';

export type tSubscriptionModalBoundaryProps = {
  /** Which subscription modal this wraps (e.g. 'manage-users'), for the Sentry tag. */
  modal: string;
  children: ReactNode;
  /**
   * RET-2032 (vuelta 2): invoked when the fallback's close control is
   * clicked, so the caller can flip its own `showXModal` state back to
   * `false` (e.g. `toggleManageUsersModal(false)`) and let the customer
   * leave the broken modal and retry. Optional — a caller that mounts this
   * boundary standalone (the vuelta 1 test suite does, with no provider
   * around it) still gets a usable, dismissible-looking fallback; only the
   * close control's click becomes a no-op for it.
   */
  onClose?: () => void;
};

/**
 * RET-2032 — containment boundary for a single subscription add-on/plan
 * modal (`ManageUsersModal` today; any sibling modal can reuse it the same
 * way). Same shape as `AlertSurfaceBoundary` (RET-1871 pattern:
 * `Sentry.ErrorBoundary` + `beforeCapture` tagging), but with one
 * deliberate difference: that boundary's fallback renders NOTHING (an alert
 * surface that fails must stay silent), while THIS fallback DOES render —
 * a modal the customer explicitly opened must tell them it broke, not
 * vanish.
 *
 * Why this exists: every subscription modal used to mount directly under
 * `AlertCenterBoundary` at the app root (`Providers.component.tsx`), whose
 * own fallback remounts the ENTIRE private tree on any catch. A throw
 * inside one modal (e.g. a dirty product catalog entry) silently bounced
 * the customer back to wherever they were — no error, no explanation. This
 * boundary is Tier 2: it catches the throw right at the modal, so the root
 * boundary never sees it and the rest of the app (including this modal's
 * own siblings) is untouched.
 *
 * RET-2032 (vuelta 2): the fallback itself used to be a bare `<p>` painted
 * at the root of the private tree, ahead of `{children}` — a stray red line
 * above the whole app, with no way to leave it, while the real modal it
 * replaces is a full-screen fixed dialog. It now reuses the same
 * `react-daisyui` `Modal` every subscription modal is built on, so the
 * fallback reads as "this dialog broke" instead of "something on the page
 * broke", and offers a close control wired to `onClose` so the customer can
 * back out and retry instead of being stuck looking at it.
 */
export default function SubscriptionModalBoundary(
  props: tSubscriptionModalBoundaryProps,
) {
  const { modal, children, onClose } = props;
  const { currentWorkspace } = useWorkspaceContext();
  const { t } = useTranslation('settings/SubscriptionManager');

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <Modal
          open
          backdrop
          onClose={onClose}
          className="rounded-lg p-0 min-w-[20rem]"
          data-testid="subscription-modal-error-dialog"
        >
          <Modal.Body className="p-4">
            <p
              className="text-sm text-error"
              data-testid="subscription-modal-error"
            >
              {t('errors.subscriptionModalUnavailable')}
            </p>
          </Modal.Body>
          <Modal.Actions className="p-4 pt-0">
            <Button
              type="button"
              size="sm"
              data-testid="subscription-modal-error-close"
              onClick={() => {
                onClose?.();
                resetError();
              }}
            >
              {t('errors.subscriptionModalUnavailableClose')}
            </Button>
          </Modal.Actions>
        </Modal>
      )}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'subscription_modal');
        scope.setTag('subscription_modal', modal);
        scope.setTag('workspace_id', currentWorkspace?.id ?? 'unknown');
        scope.setLevel('error');
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
